import { useI18n } from '@/libraries/contexts/i18n-context';
import { useMemo, useState } from 'react';
import Button from '@/components/molecules/button/button';
import FlightSelectorCard from '../flight-selector-card/flight-selector-card';
import styles from './flight-selector-card-list.module.scss';

interface FlightSelectorCardListProps {
  /** The active item */
  activeItem?: Content.Components['FlightSelectorTransportDto'] | null;
  /** The list of items */
  items: Content.Components['FlightSelectorTransportDto'][];
  /** The tooltip content to show when there is no time */
  noTimeHeadline: string;
  /** The text to show when there is no time */
  noTimeText: string;
  /** An event that is triggered on click */
  onClick: (value: Content.Components['FlightSelectorTransportDto']) => void;
}

const ITEM_LIMIT = 3;

const FlightSelectorCardList = ({
  activeItem,
  items = [],
  noTimeHeadline,
  noTimeText,
  onClick,
}: FlightSelectorCardListProps) => {
  const { t } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const activeItemIndex = items.findIndex(item => item.id === activeItem?.id);

    return activeItemIndex + 1 > ITEM_LIMIT;
  });

  const buttonText = isCollapsed
    ? t('items.common.words.showLess')
    : t('items.common.words.showMoreOptions', {
        options: items.length - ITEM_LIMIT,
      });

  const renderCards = useMemo(() => {
    const cardsToShowCount = isCollapsed ? items.length : ITEM_LIMIT;

    return items.slice(0, cardsToShowCount).map(flight => {
      const isSelectedFlight = activeItem?.id === flight.id;
      const isPriceRelative = !!activeItem && !isSelectedFlight;
      let flightPrice = flight.totalPrice;

      if (activeItem && !isSelectedFlight) {
        flightPrice = flight.totalPrice - activeItem.totalPrice;
      }

      return (
        <FlightSelectorCard
          {...flight}
          key={flight.id}
          isActive={activeItem?.id === flight.id}
          isPriceRelative={isPriceRelative}
          noTimeHeadline={noTimeHeadline}
          noTimeText={noTimeText}
          onClick={() => onClick(flight)}
          totalPrice={flightPrice}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem, isCollapsed, items]);

  return (
    <div className={styles.component}>
      {renderCards}

      {items.length > ITEM_LIMIT && (
        <Button
          className={styles.button}
          onClick={() => {
            setIsCollapsed(previous => !previous);
          }}
          text={buttonText}
          variant="secondary"
        />
      )}
    </div>
  );
};

FlightSelectorCardList.displayName = 'FlightSelectorCardList';

export default FlightSelectorCardList;
